import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import {
  CustomerUserLogInAndSecurityService,
  TwoFactorVerificationService,
} from '@wealthlane/api-services/wealthlane-customer-service';

interface UseAuthorizationVerificationParams {
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  securityOption: string | undefined;
  setEmailSent: (value: boolean) => void;
  setGateVerified: (value: boolean) => void;
  activateTargetComponent: () => void;
}

const useAuthorizationVerification = ({
  handleRequest,
  securityOption,
  setEmailSent,
  setGateVerified,
  activateTargetComponent,
}: UseAuthorizationVerificationParams) => {
  const [defaultError, setDefaultError] = useState('');
  const [recoveryError, setRecoveryError] = useState('');

  const sendOtp = useMutation(
    async () => {
      return await handleRequest(
        CustomerUserLogInAndSecurityService.postApiCustomerApiAppCustomerUserLogInAndSecuritySendCustomerUserMultiPurposeOTp()
      );
    },
    {
      onSuccess: (response: any) => {
        if (response != null) {
          Boolean(response?.success) && setEmailSent(response?.success);
        }
      },
      onError: (err) => {
        console.log('error', err);
      },
    }
  );

  const resendOtp = useMutation(
    async () => {
      return await handleRequest(
        CustomerUserLogInAndSecurityService.postApiCustomerApiAppCustomerUserLogInAndSecuritySendCustomerUserMultiPurposeOTp()
      );
    },
    {
      onSuccess: (response: any) => {
        if (response != null) {
          Boolean(response?.success) && setEmailSent(response?.success);
          toast.success(<ToastComponent label='Success' message='Resent OTP code.' />);
        }
      },
      onError: (err) => {
        console.log('error', err);
      },
    }
  );

  const verifyCode = useMutation(
    async (values: any) => {
      let input: string;
      if (securityOption === 'Security Questions') {
        input = JSON.stringify([
          { QuestionId: values?.securityQuestion1Id, Answer: values?.securityAnswer1 },
          { QuestionId: values?.securityQuestion2Id, Answer: values?.securityAnswer2 },
          { QuestionId: values?.securityQuestion3Id, Answer: values?.securityAnswer3 },
        ]);
      } else {
        input = values?.otp as string;
      }
      return await handleRequest(
        TwoFactorVerificationService.getApiCustomerApiTwoFactorVerificationCustomerVerify(input)
      );
    },
    {
      onSuccess: (response: any) => {
        if (response == null) return;

        const token = response?.token ?? null;

        if (!response.isVerified) {
          toast.error(
            <ToastComponent
              label='Error'
              message={response?.message ?? 'Verification code is not valid.'}
            />,
            { autoClose: 30000 }
          );
          return;
        }

        if (token) {
          window.sessionStorage.setItem('OtpToken', token);
        }

        if (token) {
          setGateVerified(true);
          activateTargetComponent();
        } else {
          toast.error(<ToastComponent label='Error' message='OTP response not valid.' />, {
            autoClose: 30000,
          });
        }
      },
      onError: () => {},
    }
  );

  const verifyByRecoveryCode = useMutation(
    async (values: { recoveryCode: string }) => {
      return await handleRequest(
        TwoFactorVerificationService.getApiCustomerApiTwoFactorVerificationCustomerVerifyByRecoveryCode(
          values.recoveryCode
        )
      );
    },
    {
      onSuccess: (response: any) => {
        if (response == null) return;

        const token = response?.token ?? null;

        if (!response.isVerified) {
          setRecoveryError(response?.message ?? 'Recovery code is not valid.');
          toast.error(
            <ToastComponent
              label='Error'
              message={response?.message ?? 'Recovery code is not valid.'}
            />,
            { autoClose: 30000 }
          );
          return;
        }

        if (token) {
          window.sessionStorage.setItem('OtpToken', token);
          setGateVerified(true);
          activateTargetComponent();
        } else {
          toast.error(
            <ToastComponent label='Error' message='Recovery code response not valid.' />,
            { autoClose: 30000 }
          );
        }
      },
      onError: () => {},
    }
  );

  const handleSendOtpCode = (): void => sendOtp.mutate();
  const handleResendOtpCode = (): void => resendOtp.mutate();
  const handleSubmitValue = (values: any): void => verifyCode.mutate(values);
  const handleRecoverySubmit = (values: { recoveryCode: string }): void => {
    setRecoveryError('');
    verifyByRecoveryCode.mutate(values);
  };

  return {
    defaultError,
    setDefaultError,
    recoveryError,
    setRecoveryError,
    handleSendOtpCode,
    handleResendOtpCode,
    handleSubmitValue,
    handleRecoverySubmit,
    sendOtpLoading: sendOtp.isLoading,
    verifyLoading: verifyCode.isLoading,
    recoveryLoading: verifyByRecoveryCode.isLoading,
  };
};

export default useAuthorizationVerification;
